import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import { AdminSortDirection, AdminUserSortField } from '@/api/generated';
import type { SendNudgePreviewRequest } from '@/api/types';

/** Server-side filters the ledger's ListTransactions endpoint accepts. */
export interface LedgerTransactionFilters {
  context?: string;
  status?: string;
  assetSymbol?: string;
  pageSize?: number;
  pageToken?: string;
}

/** Server-side filters the wallets ListTransactions endpoint accepts. */
export interface WalletTransactionFilters {
  chainId: number;
  status?: string;
  limit?: number;
  offset?: number;
}

export const userKeys = {
  all: ['users'] as const,
  list: (
    query: string,
    limit: number,
    offset: number,
    kycStatusFilter: string,
    platformFilter: string,
    sortField: AdminUserSortField,
    sortDirection: AdminSortDirection,
  ) =>
    [
      ...userKeys.all,
      'list',
      query,
      limit,
      offset,
      kycStatusFilter,
      platformFilter,
      sortField,
      sortDirection,
    ] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
  balances: (id: string) => [...userKeys.all, 'balances', id] as const,
  savings: (id: string) => [...userKeys.all, 'savings', id] as const,
  activity: (id: string, filters: LedgerTransactionFilters) =>
    [...userKeys.all, 'activity', id, filters] as const,
  kyc: (id: string) => [...userKeys.all, 'kyc', id] as const,
  kycData: (id: string) => [...userKeys.all, 'kycData', id] as const,
  preferences: (id: string) => [...userKeys.all, 'preferences', id] as const,
  wallets: (id: string) => [...userKeys.all, 'wallets', id] as const,
  portfolio: (id: string) => [...userKeys.all, 'portfolio', id] as const,
  walletTxns: (id: string, filters: WalletTransactionFilters) =>
    [...userKeys.all, 'walletTxns', id, filters] as const,
  linkedAccounts: (id: string) => [...userKeys.all, 'linkedAccounts', id] as const,
  recipients: (id: string) => [...userKeys.all, 'recipients', id] as const,
  devices: (id: string) => [...userKeys.all, 'devices', id] as const,
  inbox: (id: string) => [...userKeys.all, 'inbox', id] as const,
  lifecycle: (id: string) => [...userKeys.all, 'lifecycle', id] as const,
  schedules: (id: string) => [...userKeys.all, 'schedules', id] as const,
  scheduleJobs: (id: string, scheduleId: string) => [...userKeys.all, 'scheduleJobs', id, scheduleId] as const,
};

/**
 * A page of the admin users list.
 *
 * The sort is part of the request, not something applied to the response:
 * admin_api orders every matching user before it cuts the page, so page 1 of
 * "oldest first" really is the oldest signups. Sorting here instead would only
 * ever reorder the twenty-five rows already fetched.
 */
export function useUsers(
  query = '',
  limit = 25,
  offset = 0,
  kycStatusFilter = '',
  platformFilter = '',
  sortField = AdminUserSortField.ADMIN_USER_SORT_FIELD_CREATED_AT,
  sortDirection = AdminSortDirection.ADMIN_SORT_DIRECTION_DESC,
) {
  return useQuery({
    queryKey: userKeys.list(
      query,
      limit,
      offset,
      kycStatusFilter,
      platformFilter,
      sortField,
      sortDirection,
    ),
    queryFn: () =>
      adminClient.searchUsers({
        query: query || undefined,
        limit,
        offset,
        kycStatusFilter: kycStatusFilter || undefined,
        platformFilter: platformFilter || undefined,
        sortField,
        sortDirection,
      }),
  });
}

export function useUserDetail(userId: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => adminClient.getUser(userId),
    enabled: !!userId,
  });
}

export function useUserBalances(userId: string) {
  return useQuery({
    queryKey: userKeys.balances(userId),
    queryFn: () => adminClient.getUserBalances(userId),
    enabled: !!userId,
  });
}

export function useUserSavings(userId: string) {
  return useQuery({
    queryKey: userKeys.savings(userId),
    queryFn: () => adminClient.getUserSavings(userId),
    enabled: !!userId,
  });
}

export function useUserActivity(userId: string, filters: LedgerTransactionFilters = {}) {
  return useQuery({
    queryKey: userKeys.activity(userId, filters),
    queryFn: () => adminClient.getUserLedgerTransactions(userId, filters),
    enabled: !!userId,
  });
}

export function useUserKycStatus(userId: string) {
  return useQuery({
    queryKey: userKeys.kyc(userId),
    queryFn: () => adminClient.getUserKycStatus(userId),
    enabled: !!userId,
  });
}

export function useUserKycData(userId: string) {
  return useQuery({
    queryKey: userKeys.kycData(userId),
    queryFn: () => adminClient.getUserKycData(userId),
    enabled: !!userId,
  });
}

export function useUserPreferences(userId: string) {
  return useQuery({
    queryKey: userKeys.preferences(userId),
    queryFn: () => adminClient.getUserPreferences(userId),
    enabled: !!userId,
  });
}

/**
 * The chains admin can inspect, most-used first. Both the wallet fan-out below
 * and the on-chain transactions chain picker read this, so there is one list to
 * keep current rather than two that can disagree about what Liminal supports.
 */
export const ADMIN_CHAINS = [
  { id: 8453, label: 'Base' },
  { id: 42161, label: 'Arbitrum (legacy)' },
  { id: 10, label: 'Optimism' },
  { id: 1, label: 'Ethereum' },
] as const;

/** Where essentially all activity lives, so it is what the picker opens on. */
export const DEFAULT_CHAIN_ID = 8453;

const ALL_CHAIN_IDS = ADMIN_CHAINS.map((chain) => chain.id);

export function useUserWallets(userId: string) {
  return useQuery({
    queryKey: userKeys.wallets(userId),
    queryFn: async () => {
      const results = await Promise.all(
        ALL_CHAIN_IDS.map(chainId =>
          adminClient.getUserWallets(userId, chainId).catch(() => ({ wallets: [] }))
        ),
      );
      return { wallets: results.flatMap(r => r.wallets ?? []) };
    },
    enabled: !!userId,
  });
}

export function useUserWalletTransactions(userId: string, filters: WalletTransactionFilters) {
  return useQuery({
    queryKey: userKeys.walletTxns(userId, filters),
    queryFn: () => adminClient.getUserWalletTransactions(userId, filters),
    enabled: !!userId,
  });
}

export function useUserPortfolio(userId: string) {
  return useQuery({
    queryKey: userKeys.portfolio(userId),
    queryFn: () => adminClient.getUserPortfolio(userId),
    enabled: !!userId,
  });
}

export function useUserLinkedAccounts(userId: string) {
  return useQuery({
    queryKey: userKeys.linkedAccounts(userId),
    queryFn: () => adminClient.getUserLinkedAccounts(userId),
    enabled: !!userId,
  });
}

export function useUserRecipients(userId: string) {
  return useQuery({
    queryKey: userKeys.recipients(userId),
    queryFn: () => adminClient.getUserRecipients(userId),
    enabled: !!userId,
  });
}

export function useUserDevices(userId: string) {
  return useQuery({
    queryKey: userKeys.devices(userId),
    queryFn: () => adminClient.getUserDevices(userId),
    enabled: !!userId,
  });
}

export function useUserInbox(userId: string) {
  return useQuery({
    queryKey: userKeys.inbox(userId),
    queryFn: () => adminClient.getUserInbox(userId),
    enabled: !!userId,
  });
}

/** The user's onboarding nudge state (LIM-3551). */
export function useUserLifecycle(userId: string) {
  return useQuery({
    queryKey: userKeys.lifecycle(userId),
    queryFn: () => adminClient.getUserLifecycle(userId),
    enabled: !!userId,
  });
}

/** Restarts a staff test account's nudge journey. */
export function useRestartUserLifecycle(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminClient.restartUserLifecycle(userId),
    onSuccess: (lifecycle) => {
      queryClient.setQueryData(userKeys.lifecycle(userId), lifecycle);
    },
  });
}

/** Sends one nudge message to the signed-in admin's own account. */
export function useSendNudgePreview() {
  return useMutation({
    mutationFn: (request: SendNudgePreviewRequest) => adminClient.sendNudgePreview(request),
  });
}

export function useUserSchedules(userId: string) {
  return useQuery({
    queryKey: userKeys.schedules(userId),
    queryFn: () => adminClient.getUserSchedules(userId),
    enabled: !!userId,
  });
}

export function useScheduleJobs(userId: string, scheduleId: string) {
  return useQuery({
    queryKey: userKeys.scheduleJobs(userId, scheduleId),
    queryFn: () => adminClient.getScheduleJobs(userId, scheduleId),
    enabled: !!userId && !!scheduleId,
  });
}

// Writes the user's onboarding-chat-completed bit. False sends them back
// through a fresh onboarding chat on their next foreground; true lets them
// past the route guard without it. The endpoint returns the updated profile,
// so the detail cache is written directly rather than refetched.
export function useSetOnboardingChatCompleted(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (completed: boolean) =>
      adminClient.updateProfile(userId, { isOnboardingChatCompleted: completed }),
    onSuccess: (profile) => {
      queryClient.setQueryData(userKeys.detail(userId), profile);
    },
  });
}
